import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Paper,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import ScienceIcon from '@mui/icons-material/Science';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const DebugIndexPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BugReportIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Centro de Depuración de Cálculos
            </Typography>
            <Typography variant="subtitle1">
              Análisis ultra-detallado de cálculos de isótopos
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Descripción */}
      <Card sx={{ mb: 3, bgcolor: 'info.light' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Bienvenido al Centro de Depuración
          </Typography>
          <Typography variant="body1">
            Esta herramienta te permite analizar cada paso de los cálculos realizados para diferentes isótopos.
            Selecciona el isótopo que deseas depurar:
          </Typography>
        </CardContent>
      </Card>

      {/* Opciones de isótopos */}
      <Grid container spacing={3}>
        {/* Sr-89 (Método Currie) */}
        <Grid item xs={12} md={6} lg={4}>
          <Card 
            sx={{ 
              height: '100%', 
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 6,
              }
            }}
            onClick={() => navigate('/admin/debug/sr89')}
          >
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ScienceIcon sx={{ fontSize: 50, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    Sr-89
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Método Currie
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" paragraph>
                Análisis detallado del cálculo de Estroncio-89 utilizando el método Currie. 
                Incluye desglose de:
              </Typography>
              
              <Box component="ul" sx={{ pl: 2, mb: 2, flexGrow: 1 }}>
                <li>Eficiencias (Sr-89, Sr-90, Y-90)</li>
                <li>Factores exponenciales (B, C, D, E, M, N)</li>
                <li>CPM0 Sr-89 con todos sus componentes</li>
                <li>Actividad, LID e Incertidumbre</li>
                <li>Cada paso intermedio de cada cálculo</li>
              </Box>
              
              <Button 
                variant="contained" 
                fullWidth 
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/admin/debug/sr89')}
              >
                Analizar Sr-89
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Ni-63 (Método ISO 11929) */}
        <Grid item xs={12} md={6} lg={4}>
          <Card 
            sx={{ 
              height: '100%', 
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 6,
              }
            }}
            onClick={() => navigate('/admin/debug/ni63')}
          >
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ScienceIcon sx={{ fontSize: 50, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="secondary">
                    Ni-63
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Método ISO 11929
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" paragraph>
                Análisis detallado del cálculo de Níquel-63 utilizando la norma ISO 11929. 
                Incluye desglose de:
              </Typography>
              
              <Box component="ul" sx={{ pl: 2, mb: 2, flexGrow: 1 }}>
                <li>Eficiencia de detección</li>
                <li>CPM neto y factor exponencial</li>
                <li>Componentes de incertidumbre</li>
                <li>Límite crítico y LID</li>
                <li>Datos de patrones (primario, secundario, terciario)</li>
                <li>Incertidumbres relativas</li>
              </Box>
              
              <Button 
                variant="contained" 
                color="secondary"
                fullWidth 
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/admin/debug/ni63')}
              >
                Analizar Ni-63
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Alfa Total */}
        <Grid item xs={12} md={6} lg={4}>
          <Card 
            sx={{ 
              height: '100%', 
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 6,
              }
            }}
            onClick={() => navigate('/admin/debug/alfa-total')}
          >
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ScienceIcon sx={{ fontSize: 50, color: 'success.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    Alfa Total
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Método Estándar
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" paragraph>
                Análisis detallado del cálculo de Alfa Total. 
                Incluye desglose de:
              </Typography>
              
              <Box component="ul" sx={{ pl: 2, mb: 2, flexGrow: 1 }}>
                <li>Eficiencia (curva polinómica)</li>
                <li>CPM neto de la muestra</li>
                <li>Componentes de incertidumbre</li>
                <li>Límite crítico y LID</li>
                <li>Datos de calibración y patrones</li>
                <li>Incertidumbres de cantidad de muestra</li>
              </Box>
              
              <Button 
                variant="contained" 
                color="success"
                fullWidth 
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/admin/debug/alfa-total')}
              >
                Analizar Alfa Total
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Beta Total */}
        <Grid item xs={12} md={6} lg={4}>
          <Card 
            sx={{ 
              height: '100%', 
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 6,
              }
            }}
            onClick={() => navigate('/admin/debug/beta-total')}
          >
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ScienceIcon sx={{ fontSize: 50, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">
                    Beta Total
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Método Estándar
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" paragraph>
                Análisis detallado del cálculo de Beta Total. 
                Incluye desglose de:
              </Typography>
              
              <Box component="ul" sx={{ pl: 2, mb: 2, flexGrow: 1 }}>
                <li>Eficiencia (curva polinómica)</li>
                <li>CPM neto de la muestra</li>
                <li>Componentes de incertidumbre</li>
                <li>Límite crítico y LID</li>
                <li>Datos de calibración y patrones</li>
                <li>Incertidumbres de cantidad de muestra</li>
              </Box>
              
              <Button 
                variant="contained" 
                color="warning"
                fullWidth 
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/admin/debug/beta-total')}
              >
                Analizar Beta Total
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Beta Resto */}
        <Grid item xs={12} md={6} lg={4}>
          <Card 
            sx={{ 
              height: '100%', 
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 6,
              }
            }}
            onClick={() => navigate('/admin/debug/beta-resto')}
          >
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ScienceIcon sx={{ fontSize: 50, color: 'info.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="info.main">
                    Beta Resto
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Método Estándar
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" paragraph>
                Análisis detallado del cálculo de Beta Resto. 
                Incluye desglose de:
              </Typography>
              
              <Box component="ul" sx={{ pl: 2, mb: 2, flexGrow: 1 }}>
                <li>Eficiencia (curva polinómica)</li>
                <li>CPM neto de la muestra</li>
                <li>Componentes de incertidumbre</li>
                <li>Límite crítico y LID</li>
                <li>Datos de calibración y patrones</li>
                <li>Incertidumbres de cantidad de muestra</li>
              </Box>
              
              <Button 
                variant="contained" 
                color="info"
                fullWidth 
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/admin/debug/beta-resto')}
              >
                Analizar Beta Resto
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Instrucciones */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📋 Instrucciones de Uso
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                1. Selecciona el Isótopo
              </Typography>
              <Typography variant="body2">
                Haz clic en la tarjeta del isótopo que deseas analizar (Sr-89, Ni-63, Alfa Total, Beta Total o Beta Resto).
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                2. Ingresa el ID del Cálculo
              </Typography>
              <Typography variant="body2">
                Introduce el identificador del cálculo que quieres depurar en el formulario.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                3. Analiza los Resultados
              </Typography>
              <Typography variant="body2">
                Revisa cada componente y paso intermedio del cálculo. Puedes exportar los datos.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DebugIndexPage;
